import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff, Lock, Shield, LogOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (!session?.user) {
          navigate('/admin-login', { replace: true });
          return;
        }

        // Get employee info
        const { data: employee } = await supabase
          ?.from('employees')
          ?.select('name, role, must_change_password')
          ?.eq('auth_user_id', session?.user?.id)
          ?.single();

        if (employee) {
          setUserInfo({ ...employee, email: session?.user?.email });
          // If must_change_password is false, redirect to appropriate dashboard
          if (employee?.must_change_password === false) {
            redirectToDashboard(employee?.role);
            return;
          }
        } else {
          setUserInfo({ email: session?.user?.email });
        }
      } catch (err) {
        console.error('[CHANGE-PASSWORD] Auth check error:', err);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  const redirectToDashboard = (role) => {
    if (role === 'chauffeur') {
      navigate('/driver-dashboard', { replace: true });
    } else if (role === 'admin' || role === 'super_admin') {
      navigate('/admin-shipments-dashboard', { replace: true });
    } else if (role === 'agent_abidjan') {
      navigate('/dashboard-agent-abidjan', { replace: true });
    } else {
      navigate('/admin-shipments-dashboard', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Veuillez saisir un nouveau mot de passe.');
      return;
    }
    if (newPassword?.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      // Update must_change_password = false
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase
          ?.from('employees')
          ?.update({ must_change_password: false })
          ?.eq('auth_user_id', user?.id);
      }

      setSuccess(true);

      // Redirect after 2 seconds
      setTimeout(() => {
        redirectToDashboard(userInfo?.role);
      }, 2000);
    } catch (err) {
      console.error('[CHANGE-PASSWORD] Update error:', err);
      setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase?.auth?.signOut();
    navigate('/admin-login', { replace: true });
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">ADT</span>
            </div>
            <span className="text-xl font-bold text-gray-900">ADT Express</span>
          </div>
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-amber-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Changement de mot de passe requis
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant de continuer.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">

          {/* User info */}
          {userInfo && (
            <div className="bg-gray-50 rounded-lg p-3 mb-5 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-700 font-bold text-sm">
                  {userInfo?.name?.charAt(0) || userInfo?.email?.charAt(0) || '?'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{userInfo?.name || 'Utilisateur'}</p>
                <p className="text-xs text-gray-500">{userInfo?.email}</p>
              </div>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800 mb-1">Mot de passe modifié !</p>
                <p className="text-sm text-green-700">Redirection vers votre tableau de bord...</p>
              </div>
            </div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-3">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <Shield size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Ce mot de passe temporaire doit être changé. Choisissez un mot de passe personnel d'au moins 8 caractères.
                </p>
              </div>

              {/* New password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e?.target?.value)}
                    placeholder="Minimum 8 caractères"
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                    disabled={loading}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    placeholder="Répétez le mot de passe"
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && (
                  <p className={`text-xs mt-1 ${newPassword === confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                    {newPassword === confirmPassword ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || newPassword !== confirmPassword || newPassword?.length < 8}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm min-h-[44px]"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Modification en cours...</span>
                  </>
                ) : (
                  <span>Définir mon nouveau mot de passe</span>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Logout link */}
        <div className="mt-6 text-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 font-medium min-h-[44px] py-2"
          >
            <LogOut size={16} />
            <span>Se déconnecter</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
